import { ProblemDetails, isProblemDetails, getProblemErrorMessage } from './problem';

let activeCSRFToken: string | null = null;

export function setCSRFToken(token: string | null) {
  activeCSRFToken = token;
}

export function getCSRFToken(): string | null {
  return activeCSRFToken;
}

export class ApiError extends Error {
  public problem?: ProblemDetails;
  public status: number;

  constructor(message: string, status: number, problem?: ProblemDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
}

export function createApiClient(basePath: string = ''): ApiClient {
  const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${cleanBase}${cleanPath}`;

    if (options.params) {
      const searchParams = new URLSearchParams();
      for (const [key, val] of Object.entries(options.params)) {
        if (val !== undefined) {
          searchParams.append(key, String(val));
        }
      }
      const qs = searchParams.toString();
      if (qs) {
        url += (url.includes('?') ? '&' : '?') + qs;
      }
    }

    const headers = new Headers(options.headers || {});
    headers.set('Accept', 'application/json, application/problem+json');

    // Attach active or stored auth token if present
    if (!headers.has('Authorization')) {
      try {
        const storedToken = localStorage.getItem('hc_token') || localStorage.getItem('token');
        if (storedToken && storedToken !== 'null' && storedToken !== 'undefined') {
          headers.set('Authorization', `Bearer ${storedToken}`);
        }
      } catch (_) {}
    }

    // Attach active role if present
    if (!headers.has('X-Demo-Role')) {
      try {
        const storedRole = localStorage.getItem('hc_role');
        if (storedRole) {
          headers.set('X-Demo-Role', storedRole);
        }
      } catch (_) {}
    }

    // Attach active student ID for student/parent ward context if present
    if (!headers.has('X-Active-Student-ID')) {
      try {
        const activeStudentId = localStorage.getItem('hc_active_student_id');
        if (activeStudentId) {
          headers.set('X-Active-Student-ID', activeStudentId);
        }
      } catch (_) {}
    }

    const method = (options.method || 'GET').toUpperCase();
    const isUnsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

    if (isUnsafe && activeCSRFToken && !headers.has('X-CSRF-Token')) {
      headers.set('X-CSRF-Token', activeCSRFToken);
    }

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
      options.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'same-origin',
    });

    // 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');

    if (!response.ok) {
      if (response.status === 401) {
        try {
          localStorage.removeItem('hc_token');
          localStorage.removeItem('token');
        } catch (_) {}
      }
      if (isJson) {
        const data = await response.json();
        if (isProblemDetails(data)) {
          throw new ApiError(getProblemErrorMessage(data), response.status, data);
        }
        let errMsg = response.statusText;
        if (typeof data.message === 'string' && data.message) {
          errMsg = data.message;
        } else if (typeof data.error === 'string' && data.error) {
          errMsg = data.error;
        } else if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
          errMsg = data.error.message;
        } else if (data.detail && typeof data.detail === 'string') {
          errMsg = data.detail;
        }
        throw new ApiError(errMsg, response.status);
      }
      const text = await response.text();
      throw new ApiError(text || response.statusText, response.status);
    }

    if (isJson) {
      return (await response.json()) as T;
    }

    return (await response.text()) as unknown as T;
  }

  return {
    get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'POST', body: body as BodyInit }),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PUT', body: body as BodyInit }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PATCH', body: body as BodyInit }),
    delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  };
}

export const coachApi = createApiClient('/api/v1');
export const coreApi = coachApi;
